"use client";

import { Form } from "@/components/base/form";
import GoogleLogo from "@/svg/google.svg";
import { zodResolver } from "@hookform/resolvers/zod";
import { type DefaultValues, useForm } from "react-hook-form";
import type z from "zod";
import { Button } from "../base/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../base/dialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../base/form";
import { Input } from "../base/input";
import data, {
  type SignInFormSchema,
  type SignUpFormSchema,
} from "./AuthModal.data";
import { LoadingOverlay } from "./LoadingOverlay";

type AuthModalProps = {
  isOpened: boolean;
  error: React.ReactNode | null;
  isLoading: React.ReactNode | null;
  onClose: () => void;
  onSignIn: (data: SignInFormSchema) => void;
  onSignUp: (data: SignUpFormSchema) => void;
  onGoogleSignIn: () => void;
  onChange: () => void;
};

export function AuthModal({
  isOpened,
  error,
  isLoading,
  onClose,
  onSignIn,
  onSignUp,
  onGoogleSignIn,
  onChange,
}: AuthModalProps) {
  return (
    <Dialog open={isOpened} onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold">{data.title}</DialogTitle>
          <DialogDescription>{data.subtitle}</DialogDescription>
        </DialogHeader>

        <Button
          variant="outline"
          size="md"
          className="mx-auto w-full max-w-md gap-4"
          onClick={onGoogleSignIn}
        >
          <GoogleLogo className="size-5" />
          {data.googleButtonLabel}
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background text-muted-foreground px-2">
              {data.seperatorLabel}
            </span>
          </div>
        </div>

        <div className="flex flex-wrap justify-between gap-16">
          <FormSection
            section={data.signin}
            onSubmit={onSignIn}
            onChange={onChange}
          />
          <FormSection
            section={data.signup}
            onSubmit={onSignUp}
            onChange={onChange}
          />
        </div>

        {error && <div className="text-red-500">{error}</div>}
        {isLoading && <LoadingOverlay />}
      </DialogContent>
    </Dialog>
  );
}

function FormSection<
  TSection extends (typeof data)["signin"] | (typeof data)["signup"],
  TFormData extends z.infer<TSection["schema"]>,
>({
  section,
  onSubmit,
  onChange,
}: {
  section: TSection;
  onSubmit: (data: TFormData) => void;
  onChange: () => void;
}) {
  const form = useForm<TFormData>({
    resolver: zodResolver(section.schema as never),
    defaultValues: section.fields.reduce(
      (acc, field) => ({ ...acc, [field.name]: "" }),
      {} as DefaultValues<TFormData>,
    ),
  });

  return (
    <div key={section.title} className="flex min-w-72 flex-1 flex-col gap-8">
      <Form key={section.title} {...form}>
        <h3 className="text-lg font-semibold">{section.title}</h3>
        <form
          onSubmit={form.handleSubmit(async (data) => {
            await onSubmit(data);
            form.reset();
          })}
          className="space-y-8"
        >
          {section.fields.map((sectionField) => (
            <FormField
              key={sectionField.name}
              control={form.control as never}
              name={sectionField.name as never}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{sectionField.label}</FormLabel>
                  <FormControl>
                    <Input
                      type={sectionField.type}
                      placeholder={sectionField.placeholder}
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        onChange();
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          ))}
          <Button type="submit" variant="default" size="md" className="w-full">
            {section.submitButtonLabel}
          </Button>
        </form>
      </Form>
    </div>
  );
}
